import React, {useEffect, useState} from 'react';
import {CircularProgress, TableCell} from '@material-ui/core';
import {AddIcon, StoreIcon} from '@treez-ui/core/icons';
import {NumberInput, NumericalUnit, Checkbox} from '@treez-ui/core/forms';
import {formatCurrency} from '~/lib/format';
import {getDecimalPrecision} from '~/lib/numbers';
import {treezColorMap, TreezColor} from '~/styles/constants';
import {ProductStorePresenceAttributes, updateCatalogProduct} from '~/utils/ProductAPI';
import PriceTierLabel from '~/modules/widgets/PriceTierLabel';

const DECIMAL_PRECISION = 2;

interface Props {
    catalogId: number;
    defaultPrice: string;
    handleActivateOrDeactivate: (catalogId: number, status: boolean) => void;
    handleAddToStore: (catalogId: number) => void;
    isLoading: boolean;
    productId: number;
    productPresenceInCatalog: ProductStorePresenceAttributes | undefined;
    storePrice: string | undefined | null;
    storeState: boolean | undefined;
}

export const StoreMatrixCell = (props: Props) => {
    const {
        catalogId,
        defaultPrice,
        handleActivateOrDeactivate,
        handleAddToStore,
        isLoading,
        productId,
        productPresenceInCatalog,
        storePrice,
        storeState,
    } = props;

    const [value, setValue] = useState<string>(
        formatCurrency(storePrice || defaultPrice || '0.00')
    );

    useEffect(() => {
        setValue(formatCurrency(storePrice || defaultPrice || '0.00'));
    }, [storePrice, defaultPrice]);

    const onBlur = () => {
        const precision = getDecimalPrecision(value);

        if (precision > DECIMAL_PRECISION) {
            console.error('Too many decimals');
            return;
        }

        const price = value;
        setValue(formatCurrency(price));
        if (price != defaultPrice && price != storePrice && price != '') {
            try {
                updateCatalogProduct(catalogId, productId, {
                    price: price ? Number(price) : undefined,
                });
            } catch (error) {
                console.error(error);
            }
        }
    };

    const renderPriceInputOrPriceTier = () => {
        if (!productPresenceInCatalog) {
            return;
        }

        const {priceTierId} = productPresenceInCatalog;

        if (priceTierId) {
            return (
                <div className="matrix-view-cell-content-container">
                    <PriceTierLabel tierId={priceTierId} />
                    <div className="store-status-checkbox">
                        {isLoading ? (
                            <CircularProgress size={20} />
                        ) : (
                            <Checkbox
                                checked={storeState || false}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    handleActivateOrDeactivate(catalogId, isChecked);
                                }}
                            />
                        )}
                    </div>
                </div>
            );
        }
        return (
            <div className="matrix-view-cell-content-container">
                <NumberInput
                    allowDecimals
                    decimalPlaces={DECIMAL_PRECISION}
                    onBlur={onBlur}
                    onChange={(value) => setValue(value || '0.00')}
                    placeholder={`${formatCurrency(defaultPrice)}`}
                    units={NumericalUnit.DOLLARS}
                    value={value}
                />
                <div className="store-status-checkbox">
                    {isLoading ? (
                        <CircularProgress size={20} />
                    ) : (
                        <Checkbox
                            checked={storeState || false}
                            onChange={(e) => {
                                const isChecked = e.target.checked;
                                handleActivateOrDeactivate(catalogId, isChecked);
                            }}
                        />
                    )}
                </div>
            </div>
        );
    };

    return (
        <TableCell
            className="store-matrix-cell"
            key={`${catalogId}-${productId}`}
            padding="none"
            align="center"
        >
            {productPresenceInCatalog ? (
                renderPriceInputOrPriceTier()
            ) : (
                <div className="assign-to-store-button-container">
                    {isLoading ? (
                        <CircularProgress size={20} />
                    ) : (
                        <button
                            onClick={() => handleAddToStore(catalogId)}
                            className="assign-to-store-button"
                        >
                            <AddIcon color={treezColorMap[TreezColor.TREEZ_GREEN]} size={30} />
                            <StoreIcon />
                        </button>
                    )}
                </div>
            )}
        </TableCell>
    );
};

export default React.memo(StoreMatrixCell);
